import { useState } from 'react'
import dayjs from 'dayjs'
import { useQueryClient } from 'react-query'

import Styles from './RescheduleDropdown.module.scss'
import { Button } from 'blaze-ui/Button'
import { CalendarTrash, Send1 } from 'blaze-ui/icons'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { CROSS_POST_SUPPORTED_PROVIDERS } from 'api/clients/ExternalIntegrationsClient'
import { SOCIAL_VIDEO_PLATFORMS } from 'screens/Calendar/FullScreenPreview/utils'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentWorkspaceWithProject } from 'hooks/useCurrentWorkspaceWithProject'

interface RescheduleDropdownProps {
  event: AlmanacFileCalendarEvent
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  // Overrides the clear-date action label (defaults to "Unschedule" for scheduled posts and
  // "Remove date" otherwise). Pass "Unschedule" to use it regardless of the post's status.
  unscheduleLabel?: string
}

// The reschedule calendar for a calendar card: same DateTimePicker as "Posting on", plus
// Update Date / Unschedule / Post Now actions. Reuses the shared file + publish mutations
// (as PostAtCell / PostingOnMenu do). Controlled open so the card's ⋯ menu can trigger it.
export const RescheduleDropdown = ({ event, isOpen, onOpenChange, unscheduleLabel }: RescheduleDropdownProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  // Read from the workspace the update targets (see useUpdateFile), not the Redux one.
  // Unknown workspace → assume approvals are on: only the date moves.
  const isPostApprovalEnabled = useCurrentWorkspaceWithProject()?.post_approval_enabled ?? true
  const [pickerPostAt, setPickerPostAt] = useState<string | null>(event.post_at || null)
  const [isLoading, setIsLoading] = useState(false)

  const updateFileMutation = useUpdateFile(event.key)
  const { mutate: publishScheduledPost } = usePublishScheduledPost()
  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets()

  const status = event.status as PostStatusPropertyValue
  const targets = event.scheduled_post_targets ?? []
  const scheduledPostId = event.scheduled_post_id ?? targets[0]?.scheduled_post_id
  const failedTargetIds = targets.filter((target) => target.status === 'failed').map((target) => target.id)
  const hasFailedTargets = failedTargetIds.length > 0

  const provider = targets[0]?.provider
  const isCrossPost =
    !!provider && (CROSS_POST_SUPPORTED_PROVIDERS.includes(provider) || SOCIAL_VIDEO_PLATFORMS.includes(provider))
  const canPostNow = !!scheduledPostId && targets.length > 0
  const isPosted = status === PostStatusPropertyValue.Posted

  // Refresh the week grid AND the WeekView "Unscheduled" list/count, which is a separate
  // files-keyed query (['files', workspaceId, 'unscheduled']) that calendarKeys never touches —
  // without this, the count only refreshed on the next incidental refetch.
  const refreshCalendar = () => {
    queryClient.invalidateQueries(calendarKeys.all)
    queryClient.invalidateQueries([...filesKeys.all(workspaceId), 'unscheduled'])
  }

  const handleSchedule = async (date: Date) => {
    setIsLoading(true)
    try {
      // Already scheduled → only move the date. Otherwise (re)schedule it, unless approvals
      // are on and the post was never reviewed: promoting it to Autoscheduled there would
      // skip the review step — that status change must come from an approval action, which
      // stamps reviewed_at alongside it (BLZ-16596). A reviewed post that was unscheduled
      // back to Draft still carries its approval, so re-dating it re-queues it, the same
      // way the calendar's drag-to-reschedule does.
      const mayPromote = !isPostApprovalEnabled || !!event.reviewed_at
      const payload =
        status === PostStatusPropertyValue.AutoPostScheduled || !mayPromote
          ? { post_at: date.toISOString() }
          : { post_at: date.toISOString(), post_status: PostStatusPropertyValue.AutoPostScheduled }

      await updateFileMutation.mutateAsync(payload)
      showToastMessage(`Post updated for ${dayjs(date).format('ddd, MMM D, h:mma')}`, 'success')
      refreshCalendar()
    } catch {
      showToastMessage('Failed to update the post date', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnschedule = async (close: () => void) => {
    setIsLoading(true)
    try {
      // Unschedule a scheduled post = drop back to Draft (keep the date); otherwise clear the date.
      const payload =
        status === PostStatusPropertyValue.AutoPostScheduled
          ? { post_status: PostStatusPropertyValue.Draft }
          : { post_at: null }

      await updateFileMutation.mutateAsync(payload)
      showToastMessage('Post unscheduled', 'success')
      refreshCalendar()
    } catch {
      showToastMessage('Failed to unschedule the post', 'error')
    } finally {
      setIsLoading(false)
      close()
    }
  }

  const handlePostNow = (close: () => void) => {
    if (!scheduledPostId) return

    if (isCrossPost) {
      publishScheduledPostTargets({
        scheduledPostId,
        targetIds: hasFailedTargets ? failedTargetIds : undefined,
      })
    } else {
      publishScheduledPost({ id: scheduledPostId })
    }
    close()
  }

  return (
    <DateTimePicker
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="bottom-end"
      dateTime={pickerPostAt ? dayjs(pickerPostAt) : undefined}
      setDateTime={(datetime) => {
        if (datetime) setPickerPostAt(datetime.toISOString())
      }}
      actions={(close) => (
        <div className={Styles.actions}>
          <Button
            size="md"
            fullWidth
            isDisabled={isLoading || !pickerPostAt}
            onPress={() => {
              if (!pickerPostAt) return
              handleSchedule(new Date(pickerPostAt))
              close()
            }}
          >
            {event.post_at ? 'Update Date' : 'Schedule Post'}
          </Button>
          <div className={Styles.bottomActions}>
            {event.post_at && !isPosted && (
              <Button
                variant="tertiary"
                frontIcon={CalendarTrash}
                fullWidth
                size="md"
                isDisabled={isLoading}
                onPress={() => handleUnschedule(close)}
              >
                {unscheduleLabel ??
                  (status === PostStatusPropertyValue.AutoPostScheduled ? 'Unschedule' : 'Remove date')}
              </Button>
            )}
            {!isPosted && (
              <Button
                variant="tertiary"
                frontIcon={Send1}
                fullWidth
                size="md"
                isDisabled={isLoading || !canPostNow}
                onPress={() => handlePostNow(close)}
              >
                {hasFailedTargets ? 'Retry Failed' : 'Post Now'}
              </Button>
            )}
          </div>
        </div>
      )}
    >
      <span className={Styles.anchor} aria-hidden />
    </DateTimePicker>
  )
}
